import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import useTrackEvent from "@/hooks/useTrackEvent";

// ── Avatar presets (same set as EditBotProfileModal) ─────────────────────────

const DEFAULT_PICTURE =
  "https://velaromessagingstaging.blob.core.windows.net/images/default-bot-pfp.png";

const AVATAR_CATEGORIES = [
  {
    label: "🤖 AI Bots",
    avatars: [
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Velaro&backgroundColor=b6e3f4", label: "Velaro" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Atlas&backgroundColor=c0aede",  label: "Atlas" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Echo&backgroundColor=d1d4f9",   label: "Echo" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Nexus&backgroundColor=ffd5dc",  label: "Nexus" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Pixel&backgroundColor=ffdfbf",  label: "Pixel" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Orion&backgroundColor=b6e3f4",  label: "Orion" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Nova&backgroundColor=c0aede",   label: "Nova" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Cipher&backgroundColor=d1d4f9", label: "Cipher" }
    ]
  },
  {
    label: "👤 Personas",
    avatars: [
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Alex&backgroundColor=b6e3f4",   label: "Alex" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Morgan&backgroundColor=c0aede", label: "Morgan" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Riley&backgroundColor=d1d4f9",  label: "Riley" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Jamie&backgroundColor=ffd5dc",  label: "Jamie" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Taylor&backgroundColor=ffdfbf", label: "Taylor" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Jordan&backgroundColor=b6e3f4", label: "Jordan" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Casey&backgroundColor=c0aede",  label: "Casey" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Quinn&backgroundColor=d1d4f9",  label: "Quinn" }
    ]
  },
  {
    label: "✨ Illustrated",
    avatars: [
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Aria&backgroundColor=b6e3f4",  label: "Aria" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Blaze&backgroundColor=ffd5dc", label: "Blaze" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Cove&backgroundColor=d1d4f9",  label: "Cove" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Flux&backgroundColor=c0aede",  label: "Flux" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Ember&backgroundColor=ffdfbf", label: "Ember" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Sage&backgroundColor=b6e3f4",  label: "Sage" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Dusk&backgroundColor=ffd5dc",  label: "Dusk" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Zara&backgroundColor=c0aede",  label: "Zara" }
    ]
  }
];

// ── Tone presets ──────────────────────────────────────────────────────────────

const TONE_PRESETS = [
  {
    value: "friendly",
    emoji: "😊",
    label: "Friendly",
    hint: "Warm, approachable, conversational",
    prompt: "Always be warm, approachable, and conversational. Use friendly, everyday language. Make customers feel welcomed and comfortable."
  },
  {
    value: "professional",
    emoji: "💼",
    label: "Professional",
    hint: "Formal, precise, business-focused",
    prompt: "Maintain a professional and formal tone at all times. Be accurate, concise, and business-appropriate."
  },
  {
    value: "empathetic",
    emoji: "💙",
    label: "Empathetic",
    hint: "Supportive, understanding, caring",
    prompt: "Lead with empathy. Acknowledge the customer's feelings before answering. Be patient, supportive, and understanding throughout the conversation."
  },
  {
    value: "concise",
    emoji: "⚡",
    label: "Concise",
    hint: "Short, direct, no-filler",
    prompt: "Keep all responses short and direct. Get to the point immediately. Avoid unnecessary filler, preamble, or repetition."
  }
];

// ── Wizard steps ──────────────────────────────────────────────────────────────

const STEPS = [
  { number: 1, label: "Name & Face" },
  { number: 2, label: "Personality" },
  { number: 3, label: "Knowledge" },
  { number: 4, label: "Behavior" },
  { number: 5, label: "Final Review" }
];

// ── Initial wizard state ──────────────────────────────────────────────────────

function freshState() {
  return {
    // Step 1
    name: "",
    displayName: "",
    pictureUrl: "",
    greeting: "",
    // Step 2
    tone: "friendly",
    customInstructions: "",
    // Step 3
    websiteUrl: "",
    skipKnowledge: false,
    // Step 4
    handoffMode: "team" as "team" | "collect" | "bestEffort",
    escalationTeamId: undefined as number | undefined,
    conversationEscalation: true
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BuildBotWizard() {
  const api = useApi();
  const navigate = useNavigate();
  const { teams } = useTeams();
  const { track } = useTrackEvent();

  const [step, setStep] = useState(1);
  const [wizard, setWizard] = useState(freshState);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof ReturnType<typeof freshState>>(
    key: K,
    value: ReturnType<typeof freshState>[K]
  ) {
    setWizard((w) => ({ ...w, [key]: value }));
  }

  function startOver() {
    setStep(1);
    setWizard(freshState());
    setError(null);
  }

  // ── Validation per step ───────────────────────────────────────────────────

  function canAdvance(): boolean {
    if (step === 1) return wizard.name.trim().length > 0;
    if (step === 4) {
      if (wizard.handoffMode === "team") return !!wizard.escalationTeamId;
    }
    return true;
  }

  // ── Build system prompt from wizard selections ────────────────────────────

  function buildSystemPrompt(): string {
    const tonePreset = TONE_PRESETS.find((t) => t.value === wizard.tone);
    const basePrompt = tonePreset?.prompt ?? "";
    if (wizard.customInstructions.trim()) {
      return basePrompt + "\n\n" + wizard.customInstructions.trim();
    }
    return basePrompt;
  }

  // ── Launch: create BotProfile + AIConfiguration ──────────────────────────

  async function launch() {
    setLaunching(true);
    setError(null);
    try {
      // Step A: create the Bot Profile
      const formData = new FormData();
      formData.append("Id", "0");
      formData.append("name", wizard.name.trim());
      formData.append("displayName", wizard.displayName.trim() || wizard.name.trim());
      formData.append("tone", wizard.tone);
      formData.append("greeting", wizard.greeting.trim());
      formData.append("hideBotName", "false");
      formData.append("isDefault", "false");
      formData.append("aiModel", "");
      formData.append("enableVoice", "false");
      formData.append("voiceId", "");
      formData.append("voiceModel", "");
      formData.append("voiceAutoPlay", "false");

      const pictureUrl = wizard.pictureUrl || DEFAULT_PICTURE;
      formData.append("pictureUrl", pictureUrl);

      const profileResp = await api.messaging.postFormData("BotProfiles", formData);
      if (!profileResp.ok) {
        setError("Failed to create the bot profile. Please try again.");
        return;
      }
      const profile = await profileResp.json();

      // Step B: create the AI Configuration
      const aiPayload: Record<string, unknown> = {
        name: wizard.name.trim(),
        prompt: buildSystemPrompt(),
        tone: wizard.tone,
        aiModel: "",
        language: "en",
        replyLength: "medium",
        knowledgeRestriction: false,
        followUp: false,
        rateResponse: false,
        activityCheck: false,
        usageLimit: 0,
        usageLimitWarning: "",
        dataUrls: wizard.websiteUrl.trim() ? [wizard.websiteUrl.trim()] : [],
        deploymentName: "",
        vectorSearchMethod: "semantic",
        indexName: "",
        conversationEscalation: wizard.handoffMode !== "bestEffort",
        sentimentDetection: false,
        escalationRules: "",
        escalationMode: wizard.handoffMode === "collect" ? "collect" : "transfer",
        escalationTeamId: wizard.handoffMode === "team" ? wizard.escalationTeamId : undefined,
        botProfileId: profile.id
      };

      const aiResp = await api.messaging.post("AIConfiguration", aiPayload);
      if (!aiResp.ok) {
        setError("Bot profile was created but the AI configuration failed. Go to Settings → Bots to finish setup.");
        return;
      }
      const aiConfig = await aiResp.json();

      // Navigate to the bot's settings page
      track("feature_click", "Bot/WizardCompleted");
      navigate(`/ai-new/settings/general/${aiConfig.id}`);
    } catch (ex) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLaunching(false);
    }
  }

  // ── Shared UI helpers ─────────────────────────────────────────────────────

  const selectedPicture = wizard.pictureUrl || DEFAULT_PICTURE;
  const selectedTone = TONE_PRESETS.find((t) => t.value === wizard.tone) ?? TONE_PRESETS[0];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">

      {/* Header bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/bots")}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Exit wizard"
          >
            ← Back
          </button>
          <span className="text-slate-400 select-none">|</span>
          <span className="font-semibold text-slate-800">Build Your Bot</span>
        </div>
        <button
          onClick={startOver}
          className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline"
        >
          Start Over
        </button>
      </div>

      {/* Progress stepper */}
      <div className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-0">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.number}>
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    s.number < step
                      ? "bg-blue-600 text-white"
                      : s.number === step
                      ? "bg-blue-600 text-white ring-2 ring-blue-200"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {s.number < step ? "✓" : s.number}
                </div>
                <span
                  className={`text-xs whitespace-nowrap ${
                    s.number === step ? "text-blue-600 font-medium" : "text-slate-400"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 mb-5 transition-colors ${
                    s.number < step ? "bg-blue-400" : "bg-slate-200"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 flex flex-col items-center justify-start px-6 py-10">
        <div className="w-full max-w-2xl">

          {/* ── Step 1: Name & Face ─────────────────────────────────────────── */}
          {step === 1 && (
            <div className="flex flex-col gap-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-slate-800">Name & Face</h1>
                <p className="text-slate-500 mt-1">Let's start with the basics — what's your bot's name?</p>
              </div>

              {/* Live preview */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center gap-5">
                <img
                  src={selectedPicture}
                  alt="Bot avatar"
                  className="w-16 h-16 rounded-full object-cover border-2 border-slate-100 flex-shrink-0"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_PICTURE; }}
                />
                <div>
                  <div className="font-semibold text-slate-800 text-lg">
                    {wizard.displayName || wizard.name || "Your Bot"}
                  </div>
                  <div className="text-slate-500 text-sm mt-0.5 italic">
                    {wizard.greeting || `Hi! I'm ${wizard.displayName || wizard.name || "your bot"}. How can I help you today?`}
                  </div>
                </div>
              </div>

              {/* Name fields */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bot Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={wizard.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="e.g. Nova, Atlas, Support Bot"
                    maxLength={80}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    autoFocus
                  />
                  <p className="text-xs text-slate-400 mt-1">The admin label — customers won't see this unless Display Name is blank.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Display Name <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={wizard.displayName}
                    onChange={(e) => set("displayName", e.target.value)}
                    placeholder="What customers see in the chat widget"
                    maxLength={80}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Opening Message <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={wizard.greeting}
                    onChange={(e) => set("greeting", e.target.value)}
                    placeholder={`Hi! I'm ${wizard.displayName || wizard.name || "your bot"}. How can I help you today?`}
                    maxLength={300}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>

              {/* Avatar picker */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="text-sm font-medium text-slate-700 mb-3">Choose an Avatar</div>
                {AVATAR_CATEGORIES.map((cat) => (
                  <div key={cat.label} className="mb-4">
                    <div className="text-xs text-slate-400 mb-2">{cat.label}</div>
                    <div className="grid grid-cols-8 gap-2">
                      {cat.avatars.map((av) => (
                        <button
                          key={av.url}
                          type="button"
                          onClick={() => set("pictureUrl", av.url)}
                          className={`rounded-full overflow-hidden border-2 transition-all ${
                            wizard.pictureUrl === av.url
                              ? "border-blue-500 ring-2 ring-blue-200 scale-110"
                              : "border-transparent hover:border-slate-300"
                          }`}
                          title={av.label}
                        >
                          <img
                            src={av.url}
                            alt={av.label}
                            className="w-10 h-10 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Personality ─────────────────────────────────────────── */}
          {step === 2 && (
            <div className="flex flex-col gap-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-slate-800">Personality</h1>
                <p className="text-slate-500 mt-1">How should your bot talk to your customers?</p>
              </div>

              {/* Tone cards */}
              <div className="grid grid-cols-2 gap-3">
                {TONE_PRESETS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => set("tone", t.value)}
                    className={`bg-white border-2 rounded-xl p-4 text-left transition-all hover:shadow-sm ${
                      wizard.tone === t.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="text-2xl mb-1">{t.emoji}</div>
                    <div className="font-semibold text-slate-800">{t.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.hint}</div>
                  </button>
                ))}
              </div>

              {/* Custom instructions */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Additional Instructions <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={wizard.customInstructions}
                  onChange={(e) => set("customInstructions", e.target.value)}
                  placeholder="Always end responses with a helpful follow-up question. Never use jargon. Refer customers to our team for refunds over $100."
                  maxLength={1000}
                  rows={4}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Plain English. These are added on top of the tone you picked.
                  {wizard.customInstructions.length > 0 && ` (${wizard.customInstructions.length}/1000)`}
                </p>
              </div>

              {/* Preview of what the bot will say */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                <strong>Preview:</strong>{" "}
                {selectedTone.hint}
                {wizard.customInstructions.trim() && ` + custom instructions.`}
                {" "}You can refine this further in Settings after launch.
              </div>
            </div>
          )}

          {/* ── Step 3: Knowledge ──────────────────────────────────────────── */}
          {step === 3 && (
            <div className="flex flex-col gap-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-slate-800">Teach It Your Business</h1>
                <p className="text-slate-500 mt-1">Give your bot something to talk about. You can add more after launch.</p>
              </div>

              {/* Quick website URL */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">🌐</span>
                    <span className="font-medium text-slate-800">Your Website</span>
                    <span className="text-xs text-slate-400 ml-auto">Crawls your pages and builds knowledge automatically</span>
                  </div>
                  <input
                    type="url"
                    value={wizard.websiteUrl}
                    onChange={(e) => set("websiteUrl", e.target.value)}
                    placeholder="https://yoursite.com"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>

              {/* More sources hint */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="text-sm font-medium text-slate-700 mb-3">More knowledge sources (available after launch)</div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { emoji: "📄", label: "Documents", hint: "PDFs, Word, Excel" },
                    { emoji: "🛍", label: "Products",  hint: "Shopify or BigCommerce" },
                    { emoji: "📹", label: "Videos",    hint: "YouTube channels" },
                    { emoji: "📚", label: "Knowledge Base", hint: "Auto-synced articles" },
                    { emoji: "📁", label: "SharePoint", hint: "Browse & select files" },
                    { emoji: "💬", label: "Overrides",  hint: "Pin exact policy facts" }
                  ].map((src) => (
                    <div key={src.label} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <span className="text-lg">{src.emoji}</span>
                      <div>
                        <div className="text-sm font-medium text-slate-700">{src.label}</div>
                        <div className="text-xs text-slate-400">{src.hint}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center text-sm text-slate-400">
                No knowledge yet? That&apos;s fine — skip for now and add sources from the bot&apos;s Settings page.
              </div>
            </div>
          )}

          {/* ── Step 4: Limits / Behavior ──────────────────────────────────── */}
          {step === 4 && (
            <div className="flex flex-col gap-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-slate-800">Set Its Limits</h1>
                <p className="text-slate-500 mt-1">What should your bot do when it can&apos;t help someone?</p>
              </div>

              {/* Handoff mode cards */}
              <div className="flex flex-col gap-3">
                {[
                  {
                    mode: "team" as const,
                    emoji: "👥",
                    label: "Transfer to a live agent",
                    hint: "When the bot can't help, it hands the customer to your team."
                  },
                  {
                    mode: "collect" as const,
                    emoji: "📋",
                    label: "Collect contact info",
                    hint: "Show a form to collect name, email, and message. No agent needed."
                  },
                  {
                    mode: "bestEffort" as const,
                    emoji: "🤖",
                    label: "Just do its best",
                    hint: "The bot always responds — no handoff, no fallback form."
                  }
                ].map((opt) => (
                  <button
                    key={opt.mode}
                    type="button"
                    onClick={() => set("handoffMode", opt.mode)}
                    className={`bg-white border-2 rounded-xl p-4 text-left flex items-start gap-3 transition-all hover:shadow-sm ${
                      wizard.handoffMode === opt.mode
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <span className="text-2xl mt-0.5">{opt.emoji}</span>
                    <div>
                      <div className="font-semibold text-slate-800">{opt.label}</div>
                      <div className="text-sm text-slate-500 mt-0.5">{opt.hint}</div>
                    </div>
                    <div className={`ml-auto mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                      wizard.handoffMode === opt.mode
                        ? "bg-blue-500 border-blue-500"
                        : "border-slate-300"
                    }`} />
                  </button>
                ))}
              </div>

              {/* Team selector when "transfer to team" is chosen */}
              {wizard.handoffMode === "team" && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Which team? <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={wizard.escalationTeamId ?? ""}
                    onChange={(e) =>
                      set("escalationTeamId", e.target.value ? Number(e.target.value) : undefined)
                    }
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">— Select a team —</option>
                    {(teams ?? []).map((t: { id: number; name: string }) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {(teams ?? []).length === 0 && (
                    <p className="text-xs text-slate-400 mt-1">
                      No teams found. Create a team first at Settings → Teams, then come back.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Step 5: Final Review ────────────────────────────────────────── */}
          {step === 5 && (
            <div className="flex flex-col gap-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-slate-800">
                  Meet {wizard.displayName || wizard.name || "Your Bot"}!
                </h1>
                <p className="text-slate-500 mt-1">Review what you&apos;ve built. Click <strong>Go to Settings</strong> to finish setup and connect your bot.</p>
              </div>

              {/* Summary card */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Bot identity header */}
                <div className="flex items-center gap-4 p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-slate-100">
                  <img
                    src={selectedPicture}
                    alt="Bot avatar"
                    className="w-16 h-16 rounded-full border-2 border-white shadow"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_PICTURE; }}
                  />
                  <div>
                    <div className="text-xl font-bold text-slate-800">
                      {wizard.displayName || wizard.name}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5 italic">
                      {wizard.greeting ||
                        `Hi! I'm ${wizard.displayName || wizard.name}. How can I help you today?`}
                    </div>
                  </div>
                </div>

                {/* Summary rows */}
                <div className="divide-y divide-slate-100">
                  <SummaryRow
                    label="Personality"
                    value={`${selectedTone.emoji} ${selectedTone.label}${wizard.customInstructions.trim() ? " + custom instructions" : ""}`}
                  />
                  <SummaryRow
                    label="Knowledge"
                    value={
                      wizard.websiteUrl.trim()
                        ? `Website: ${wizard.websiteUrl.trim()}`
                        : "No sources added — can be added in Settings"
                    }
                  />
                  <SummaryRow
                    label="When it can't help"
                    value={
                      wizard.handoffMode === "team"
                        ? `Transfer to ${
                            (teams ?? []).find((t: { id: number; name: string }) => t.id === wizard.escalationTeamId)?.name ?? "a team"
                          }`
                        : wizard.handoffMode === "collect"
                        ? "Collect contact info"
                        : "Bot does its best — no fallback"
                    }
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* What happens next */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                <strong>What happens next:</strong> Clicking <strong>Go to Settings</strong> creates your bot and takes you to its Configuration page — pre-populated with everything you set here. From there you can connect a knowledge index, test it in the Bot Playground, and adjust handoff rules.
              </div>
            </div>
          )}

          {/* ── Navigation buttons ─────────────────────────────────────────── */}
          <div className="flex items-center justify-between mt-8">
            <div>
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep((s) => s - 1)}
                  className="px-5 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  ← Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Skip (only on optional steps) */}
              {step === 3 && (
                <button
                  type="button"
                  onClick={() => setStep(4)}
                  className="px-5 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Skip for now
                </button>
              )}

              {step < 5 ? (
                <button
                  type="button"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canAdvance()}
                  className="px-6 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Continue →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={launch}
                  disabled={launching}
                  className="px-8 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors font-medium"
                >
                  {launching ? "Creating bot…" : "Go to Settings →"}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Small summary row component ───────────────────────────────────────────────

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-4 px-6 py-3">
      <span className="text-xs text-slate-400 w-28 flex-shrink-0 pt-0.5 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-sm text-slate-700">{value}</span>
    </div>
  );
}
